// Theme-world audio — a shared Web Audio engine + per-world cue palettes.
//
// Generalised from NeonOracleScenarioSim's `OracleAudio` class and the obsidian
// design-exploration mockup's noir Web Audio. ONE shared engine provides the
// synthesis primitives (`tone` / `noise` / `sweep` / `sub`); each world supplies
// a cue palette keyed by a small generic vocabulary so both bespoke renderers and
// future Tier-B wraps can fire the same cue names.
//
// Contract (matches THEME_WORLDS_PLAN §3.3):
//   - lazily-created shared AudioContext, resumed on first gesture
//   - master gain + optional feedback-delay "reverb" send
//   - MUTED BY DEFAULT; one-shots only (no loops/ambience); cleaned up on unmount
//   - `useThemeWorldAudio(worldId)` → { enabled, toggle, play(cue) }

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ThemeId } from '../../components/course-builder/slides/slideTypes';

// ─────────────────────────────────────────────────────────────────────────────
// Generic cue vocabulary — every world implements all six.
// ─────────────────────────────────────────────────────────────────────────────

export type CueName = 'select' | 'commit' | 'reveal' | 'advance' | 'reset' | 'hover';

// ─────────────────────────────────────────────────────────────────────────────
// Synthesis primitive option shapes.
// ─────────────────────────────────────────────────────────────────────────────

export interface ToneOpts {
  type?: OscillatorType;
  freq?: number;
  freqTo?: number | null;
  /** start offset (s) relative to now */
  t?: number;
  dur?: number;
  vol?: number;
  detune?: number;
  /** 0..1 fraction sent to the reverb tail */
  send?: number;
  attack?: number;
}

export interface NoiseOpts {
  t?: number;
  dur?: number;
  vol?: number;
  send?: number;
  filterType?: BiquadFilterType;
  freq?: number;
  freqTo?: number | null;
  q?: number;
  attack?: number;
  /** noise envelope curve — 'flat' (white) or 'decay' (percussive burst) */
  shape?: 'flat' | 'decay';
}

export interface SweepOpts extends NoiseOpts {
  /** sweeps require a destination frequency */
  freqTo: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Engine — shared AudioContext, master gain, feedback-delay reverb send.
// Exposes the four primitives the cue palettes are written against.
// ─────────────────────────────────────────────────────────────────────────────

export class ThemeWorldAudioEngine {
  private ac: AudioContext | null = null;
  private master: GainNode | null = null;
  private fxSend: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private readonly masterVol: number;

  constructor(masterVol = 0.16) {
    this.masterVol = masterVol;
  }

  /** Lazily create + resume the shared AudioContext. Returns null if unavailable. */
  private ensureAC(): AudioContext | null {
    if (!this.ac) {
      try {
        const Ctor: typeof AudioContext | undefined =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        const ac = new Ctor();
        const master = ac.createGain();
        master.gain.value = this.masterVol;
        master.connect(ac.destination);

        // simple feedback delay = cheap "holographic"/room reverb tail
        const delay = ac.createDelay(1.0);
        delay.delayTime.value = 0.18;
        const fb = ac.createGain();
        fb.gain.value = 0.3;
        const wet = ac.createGain();
        wet.gain.value = 0.5;
        const damp = ac.createBiquadFilter();
        damp.type = 'lowpass';
        damp.frequency.value = 3200;
        const fxSend = ac.createGain();
        fxSend.gain.value = 1;
        fxSend.connect(delay);
        delay.connect(damp);
        damp.connect(fb);
        fb.connect(delay); // feedback loop
        damp.connect(wet);
        wet.connect(master); // wet out

        this.ac = ac;
        this.master = master;
        this.fxSend = fxSend;
      } catch {
        this.ac = null;
      }
    }
    if (this.ac && this.ac.state === 'suspended') void this.ac.resume();
    return this.ac;
  }

  /** Resume context on a user gesture (called by the toggle / first interaction). */
  resume(): void {
    this.ensureAC();
  }

  private getNoise(c: AudioContext): AudioBuffer {
    if (!this.noiseBuf) {
      const len = Math.floor(c.sampleRate * 1.0);
      const buf = c.createBuffer(1, len, c.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuf = buf;
    }
    return this.noiseBuf;
  }

  /** one-shot oscillator tone with SOFT attack + exponential tail. */
  tone(opts: ToneOpts): void {
    const c = this.ensureAC();
    if (!c || !this.master) return;
    const {
      type = 'sine',
      freq = 440,
      freqTo = null,
      t = 0,
      dur = 0.08,
      vol = 0.5,
      detune = 0,
      send = 0.35,
      attack = 0.012,
    } = opts;
    const t0 = c.currentTime + t;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.detune.value = detune;
    o.frequency.setValueAtTime(freq, t0);
    if (freqTo) o.frequency.exponentialRampToValueAtTime(freqTo, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(vol, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g);
    g.connect(this.master);
    if (send > 0 && this.fxSend) {
      const s = c.createGain();
      s.gain.value = send;
      g.connect(s);
      s.connect(this.fxSend);
    }
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  /** sub-bass sine for weight/impact (soft send, slow attack defaults). */
  sub(opts: ToneOpts): void {
    this.tone({ type: 'sine', send: 0.15, attack: 0.02, ...opts });
  }

  /** one-shot FILTERED NOISE — flat (held) or percussive decay envelope. */
  noise(opts: NoiseOpts): void {
    const c = this.ensureAC();
    if (!c || !this.master) return;
    const {
      t = 0,
      dur = 0.4,
      vol = 0.3,
      send = 0.45,
      filterType = 'bandpass',
      freq = 1200,
      freqTo = null,
      q = 0.8,
      attack = 0.04,
      shape = 'flat',
    } = opts;
    const t0 = c.currentTime + t;
    const src = c.createBufferSource();
    src.buffer = this.getNoise(c);
    const f = c.createBiquadFilter();
    f.type = filterType;
    f.Q.value = q;
    f.frequency.setValueAtTime(freq, t0);
    if (freqTo) f.frequency.exponentialRampToValueAtTime(freqTo, t0 + dur);
    const g = c.createGain();
    if (shape === 'decay') {
      // percussive burst — instant onset, fast decay (e.g. pin-click, impact)
      g.gain.setValueAtTime(vol, t0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    } else {
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.linearRampToValueAtTime(vol, t0 + attack); // soft swell in
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    }
    src.connect(f);
    f.connect(g);
    g.connect(this.master);
    if (send > 0 && this.fxSend) {
      const s = c.createGain();
      s.gain.value = send;
      g.connect(s);
      s.connect(this.fxSend);
    }
    src.start(t0);
    src.stop(t0 + dur + 0.05);
  }

  /** one-shot filtered-noise SWEEP — filter glides freq→freqTo for motion. */
  sweep(opts: SweepOpts): void {
    this.noise(opts);
  }

  /** Tear down the AudioContext on unmount. */
  dispose(): void {
    if (this.ac) {
      void this.ac.close().catch(() => undefined);
      this.ac = null;
      this.master = null;
      this.fxSend = null;
      this.noiseBuf = null;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Cue palettes — a palette maps each CueName to a recipe played on the engine.
// ─────────────────────────────────────────────────────────────────────────────

export type CuePalette = Record<CueName, (e: ThemeWorldAudioEngine) => void>;

// ── Neon Oracle — sci-fi (sine/triangle + sweeps + sub + reverb tail) ─────────
// Ported from OracleAudio: select / consult / glitch / powerDown / hover / accent.

const neonOracle: CuePalette = {
  // CHOICE SELECT: clean ascending two-tone UI confirm with reverb tail.
  select(e) {
    // detuned pair for a wide shimmer
    e.tone({ type: 'sine', freq: 587, freqTo: 784, dur: 0.16, vol: 0.22, send: 0.5, attack: 0.008, detune: 7 });
    e.tone({ type: 'sine', freq: 587, freqTo: 784, dur: 0.16, vol: 0.187, send: 0.5, attack: 0.008, detune: -7 });
    e.tone({ type: 'triangle', freq: 880, freqTo: 1175, t: 0.07, dur: 0.2, vol: 0.16, send: 0.55, attack: 0.006 });
    e.sub({ freq: 196, dur: 0.18, vol: 0.12 });
  },
  // COMMIT / "consulting the oracle": rising whoosh + swelling pad + sub pulse.
  commit(e) {
    e.sweep({ t: 0, dur: 0.6, vol: 0.22, filterType: 'bandpass', freq: 320, freqTo: 4200, q: 1.0, send: 0.5, attack: 0.12 });
    e.tone({ type: 'sine', freq: 220, freqTo: 440, t: 0, dur: 0.6, vol: 0.13, detune: 9, send: 0.5, attack: 0.2 });
    e.tone({ type: 'sine', freq: 220, freqTo: 440, t: 0, dur: 0.6, vol: 0.11, detune: -9, send: 0.5, attack: 0.2 });
    e.tone({ type: 'triangle', freq: 330, freqTo: 660, t: 0.05, dur: 0.55, vol: 0.09, send: 0.55, attack: 0.18 });
    e.sub({ freq: 70, freqTo: 130, dur: 0.55, vol: 0.16 });
  },
  // CONSEQUENCE REVEAL: cinematic impact + bright airy shimmer sweep.
  reveal(e) {
    e.sub({ freq: 98, dur: 0.5, vol: 0.16 });
    e.sweep({ t: 0.02, dur: 0.6, vol: 0.1, filterType: 'bandpass', freq: 900, freqTo: 2600, q: 0.7, send: 0.55, attack: 0.06 });
    e.tone({ type: 'sine', freq: 392, freqTo: 523, dur: 0.55, vol: 0.14, detune: 6, send: 0.6, attack: 0.03 });
    e.tone({ type: 'sine', freq: 392, freqTo: 523, dur: 0.55, vol: 0.119, detune: -6, send: 0.6, attack: 0.03 });
  },
  // soft accent for staggered reveals (heading materialises, choices in).
  advance(e) {
    e.tone({ type: 'sine', freq: 660, dur: 0.22, vol: 0.1, send: 0.4 });
  },
  // RESTART ORACLE: smooth DESCENDING filtered sweep (system power-down).
  reset(e) {
    e.sweep({ dur: 0.5, vol: 0.16, filterType: 'lowpass', freq: 3600, freqTo: 260, q: 0.8, send: 0.5, attack: 0.02 });
    e.tone({ type: 'sine', freq: 660, freqTo: 130, dur: 0.45, vol: 0.16, detune: 7, send: 0.5, attack: 0.01 });
    e.tone({ type: 'sine', freq: 660, freqTo: 130, dur: 0.45, vol: 0.136, detune: -7, send: 0.5, attack: 0.01 });
    e.sub({ freq: 160, freqTo: 55, dur: 0.5, vol: 0.14 });
  },
  // CHOICE HOVER: soft airy "interface tick".
  hover(e) {
    e.tone({ type: 'sine', freq: 2100, dur: 0.06, vol: 0.05, send: 0.3, attack: 0.004 });
    e.noise({ dur: 0.08, vol: 0.025, filterType: 'highpass', freq: 4200, q: 0.6, send: 0.3 });
  },
};

// ── Obsidian Dossier — noir (pin-clicks, paper shuffle, stamp thud, cello) ────
// Ported from the obsidian mockup's Web Audio. All restrained, weighty, sparse.

const obsidian: CuePalette = {
  // SELECT = soft pin-push: sharp pinhead click transient + tiny low cork thud.
  select(e) {
    e.noise({ dur: 0.012, vol: 0.9, filterType: 'bandpass', freq: 4200, q: 5, send: 0.15, shape: 'decay' });
    e.tone({ type: 'sine', freq: 150, dur: 0.06, vol: 0.5, send: 0.1 });
  },
  // COMMIT = heavy stamp thud: deep sine thud (~110→40Hz) + noise ink-pad impact.
  commit(e) {
    e.sub({ freq: 110, freqTo: 40, dur: 0.5, vol: 1.0, send: 0.2 });
    e.noise({ dur: 0.08, vol: 0.85, filterType: 'lowpass', freq: 380, q: 0.8, send: 0.2, shape: 'decay' });
  },
  // REVEAL = single one-shot low cello/drone swell — two detuned low oscillators,
  // slow swell up then decay, minor/ominous. Plays once, no loop.
  reveal(e) {
    e.tone({ type: 'sawtooth', freq: 55, dur: 0.95, vol: 0.42, send: 0.3, attack: 0.32 });
    e.tone({ type: 'triangle', freq: 82, dur: 0.95, vol: 0.34, send: 0.3, attack: 0.32 });
  },
  // ADVANCE = lighter paper rustle (e.g. redactions lifting).
  advance(e) {
    e.noise({ dur: 0.4, vol: 0.5, filterType: 'highpass', freq: 1800, q: 0.7, send: 0.25 });
  },
  // RESET = soft reverse-ish paper shuffle to clear the board.
  reset(e) {
    e.noise({ dur: 0.18, vol: 0.4, filterType: 'lowpass', freq: 900, q: 0.6, send: 0.2 });
    e.tone({ type: 'sine', freq: 120, freqTo: 70, dur: 0.2, vol: 0.3, send: 0.15 });
  },
  // HOVER = faint low-passed paper-handling shuffle.
  hover(e) {
    e.noise({ dur: 0.07, vol: 0.28, filterType: 'lowpass', freq: 900, q: 0.6, send: 0.2 });
  },
};

// ── Aurora Drift — calm/airy (sine singing-bowls + glass chimes, long decays) ─
// Ported from the aurora-drift mockup's Web Audio: pure SINE bells, gentle
// attacks, long soft decays, a touch of detune shimmer. NO sci-fi/noir timbres.
//
// The mockup's `bell()` = fundamental + a soft octave-ish partial. The shared
// engine has single-oscillator `tone`, so each bell is two layered `tone` calls
// (fundamental at full vol + partial an octave up at ~28% mix). Long `dur` gives
// the singing-bowl tail; the engine's reverb send adds shimmer.

// A singing-bowl bell: sine fundamental + soft sine partial, gentle swell, long tail.
function bell(
  e: ThemeWorldAudioEngine,
  freq: number,
  opts: { dur?: number; vol?: number; t?: number; partial?: number; partialMix?: number; detune?: number } = {},
): void {
  const { dur = 2.4, vol = 0.16, t = 0, partial = 2.01, partialMix = 0.28, detune = 0 } = opts;
  // gentle attack (~0.05s swell), generous reverb send for the airy shimmer
  e.tone({ type: 'sine', freq, dur, vol, t, detune, send: 0.55, attack: 0.05 });
  e.tone({ type: 'sine', freq: freq * partial, dur, vol: vol * partialMix, t, detune, send: 0.6, attack: 0.05 });
}

const aurora: CuePalette = {
  // SELECT = soft sine pluck (a glass droplet). Short, gentle. Pitch is fixed
  // here (mid-range); the bespoke renderer fires pitch-tracked plucks directly
  // for the slider drag, but the generic cue stays calm + neutral.
  select(e) {
    bell(e, 523.25, { dur: 0.55, vol: 0.05, partial: 3.0, partialMix: 0.12 });
  },
  // COMMIT / lock = warm TWO-NOTE bell: root + a soft perfect fifth, gentle.
  commit(e) {
    bell(e, 392, { dur: 2.4, vol: 0.1 });
    bell(e, 392 * 1.5, { dur: 2.6, vol: 0.075, t: 0.1 });
  },
  // REVEAL = THE SHIFT BLOOM: a shimmering ASCENDING arpeggio of stacked sine
  // partials with long soft decay + gentle detune shimmer. (The bespoke renderer
  // calls this — and can scale it by magnitude via playShiftBloom below.)
  reveal(e) {
    const root = 523.25; // C5
    const ratios = [1, 1.25, 1.5, 2, 3];
    ratios.forEach((r, n) => {
      const detune = (n % 2 ? 1 : -1) * 7;
      bell(e, root * r, { dur: 1.9, vol: 0.06, t: n * 0.11, partialMix: 0.22, detune });
    });
  },
  // ADVANCE = gentle resolving consonant chord (major triad + octave), soft + slow.
  advance(e) {
    const root = 392; // G4
    [1, 1.25, 1.5, 2].forEach((r, n) => {
      bell(e, root * r, { dur: 3.0, vol: 0.06, t: n * 0.07, partialMix: 0.22 });
    });
  },
  // RESET = soft single low bell to clear the field.
  reset(e) {
    bell(e, 261.63, { dur: 2.0, vol: 0.08, partialMix: 0.2 });
  },
  // HOVER = faint high shimmer.
  hover(e) {
    bell(e, 1046.5, { dur: 0.5, vol: 0.03, partial: 2.0, partialMix: 0.1 });
  },
};

// ── Aurora extras: cue helpers that need a runtime parameter ──────────────────
// (The bespoke renderer reaches for these via the engine directly; the generic
// CuePalette stays parameter-free so Tier-B wraps can fire it blind.)

/** Soft sine pluck whose pitch tracks a 0..1 confidence fraction (220Hz→880Hz). */
export function playAuroraPluck(e: ThemeWorldAudioEngine, frac: number): void {
  const f = Math.max(0, Math.min(1, frac));
  const freq = 220 * Math.pow(2, f * 2);
  bell(e, freq, { dur: 0.55, vol: 0.05, partial: 3.0, partialMix: 0.12 });
}

/**
 * THE SHIFT BLOOM, magnitude-scaled. `intensity` (0.2..1.5) maps the size of the
 * confidence shift onto brightness/length: a bigger shift = fuller, longer,
 * brighter bloom (one extra shimmering partial past ~0.9). Built from stacked
 * one-shot bells — no loops.
 */
export function playAuroraShiftBloom(e: ThemeWorldAudioEngine, intensity = 1): void {
  const i = Math.max(0.2, Math.min(1.5, intensity));
  const ratios = [1, 1.25, 1.5, 2];
  if (i > 0.9) ratios.push(3); // bigger shift → one more shimmering partial
  const root = 523.25; // C5
  const tail = 1.6 + i * 1.4; // bigger shift → longer decay
  ratios.forEach((r, n) => {
    const detune = (n % 2 ? 1 : -1) * (4 + i * 6);
    bell(e, root * r, { dur: tail, vol: 0.045 + 0.04 * i, t: n * 0.11, partialMix: 0.22, detune });
  });
}

// ── Terra — earthy/organic (foley-style: noise rustle + warm low sines, no chiptune) ─
// Ported from the terra-solarpunk mockup's organic Web Audio: everything is built
// from filtered NOISE (natural texture) + soft low SINES (warm wood/soil body) —
// deliberately no bright/clean plucks or melodic scales (those read as 8-bit).
// Successive sprouts brighten only via filter cutoff, not rising pitch.

/** THE SPROUT — an earthy unfurl: low rustle whoosh + warm soil/wood body + a
 *  tiny water droplet. `step` brightens the rustle's cutoff per layer (not pitch),
 *  matching the mockup's "clearer message ⇒ more alive plant". */
export function playTerraSprout(e: ThemeWorldAudioEngine, step = 0): void {
  const s = Math.max(0, step);
  // rustle/whoosh — low-passed noise that opens a little per layer
  e.sweep({ dur: 0.36, vol: 0.2, filterType: 'lowpass', freq: 380 + s * 90, freqTo: 900 + s * 120, q: 0.6, send: 0.3, attack: 0.03 });
  // warm soil/wood body — low sine, gentle upward bend (warmth from low freq, not a filter)
  e.tone({ type: 'sine', freq: 150 + s * 8, freqTo: (150 + s * 8) * 1.06, dur: 0.52, vol: 0.18, detune: 8, send: 0.25, attack: 0.05 });
  // tiny water droplet — sine with a fast downward pitch drop
  e.tone({ type: 'sine', freq: 580, freqTo: 250, dur: 0.12, vol: 0.14, send: 0.2, attack: 0.005 });
}

const terra: CuePalette = {
  // SELECT = a soft leaf-tick: short band-limited noise blip, quiet + organic.
  select(e) {
    e.noise({ dur: 0.05, vol: 0.18, filterType: 'bandpass', freq: 1500, q: 1.4, send: 0.2, shape: 'decay' });
  },
  // COMMIT / "grow this layer" = the earthy sprout (default, step 0).
  commit(e) {
    playTerraSprout(e, 0);
  },
  // REVEAL / fully-bloomed = a WARM ORGANIC SWELL: slightly-inharmonic low sine
  // partials (warm pad, gentle swell, long decay) under an airy noise breeze.
  reveal(e) {
    const root = 165;
    [1, 1.5, 2.01, 2.98].forEach((p, i) => {
      e.tone({ type: 'sine', freq: root * p, dur: 1.6 + i * 0.2, vol: 0.13 - i * 0.02, detune: 14, send: 0.45, attack: 0.18 + i * 0.05, t: i * 0.04 });
    });
    e.sweep({ dur: 1.5, vol: 0.1, filterType: 'lowpass', freq: 600, freqTo: 1400, q: 0.5, send: 0.4, attack: 0.3 });
  },
  // ADVANCE = a single soft warm body note (quiet accent).
  advance(e) {
    e.tone({ type: 'sine', freq: 196, dur: 0.5, vol: 0.1, detune: 10, send: 0.3, attack: 0.06 });
  },
  // RESET = an earthy settle: low descending sine sink + darkening downward rustle.
  reset(e) {
    e.tone({ type: 'sine', freq: 220, freqTo: 132, dur: 0.6, vol: 0.16, detune: 12, send: 0.3, attack: 0.04 });
    e.sweep({ dur: 0.45, vol: 0.1, filterType: 'lowpass', freq: 700, freqTo: 260, q: 0.6, send: 0.25, attack: 0.03 });
  },
  // HOVER = faint leaf-tick.
  hover(e) {
    e.noise({ dur: 0.06, vol: 0.08, filterType: 'lowpass', freq: 900, q: 0.6, send: 0.2 });
  },
};

// ── Hyperpop Arcade — chiptune (square/triangle, dry, snappy, NO reverb send) ─
// Ported from the hyperpop-arcade mockup's Web Audio. Unlike Terra/Neon (which
// were deliberately de-chiptuned), the arcade IS chiptune by design: bright
// square-wave dings, coin pickups, a ka-chunk slam, a comedic womp, a jackpot
// arpeggio. All dry (send 0) so they stay snappy.

/** WIN / JACKPOT — fast ascending major arpeggio + triangle pad + ta-da accent. */
export function playHyperpopWin(e: ThemeWorldAudioEngine): void {
  [523, 659, 784, 1047, 1319, 1568, 2093].forEach((f, i) =>
    e.tone({ type: 'square', freq: f, t: i * 0.06, dur: 0.16, vol: 0.11, send: 0 }),
  );
  [523, 659, 784].forEach((f) => e.tone({ type: 'triangle', freq: f, dur: 0.55, vol: 0.06, send: 0 }));
  e.tone({ type: 'square', freq: 1047, t: 0.46, dur: 0.12, vol: 0.12, send: 0 });
  e.tone({ type: 'square', freq: 1568, t: 0.46, dur: 0.3, vol: 0.12, send: 0 });
}

/** LOSE — comedic descending womp/bonk (buzzy downward pitch-bend). */
export function playHyperpopLose(e: ThemeWorldAudioEngine): void {
  e.tone({ type: 'square', freq: 330, freqTo: 90, dur: 0.22, vol: 0.15, send: 0 });
  e.tone({ type: 'triangle', freq: 247, freqTo: 70, t: 0.1, dur: 0.26, vol: 0.13, send: 0 });
}

/** Single odometer increment tick. */
export function playHyperpopTick(e: ThemeWorldAudioEngine): void {
  e.tone({ type: 'square', freq: 1175, dur: 0.03, vol: 0.08, send: 0 });
}

/** Odometer LANDS — bright two-tone ding. */
export function playHyperpopLand(e: ThemeWorldAudioEngine): void {
  e.tone({ type: 'square', freq: 1319, dur: 0.06, vol: 0.12, send: 0 });
  e.tone({ type: 'square', freq: 1976, t: 0.05, dur: 0.14, vol: 0.12, send: 0 });
}

const hyperpop: CuePalette = {
  // SELECT = classic two-tone ascending coin DING.
  select(e) {
    e.tone({ type: 'square', freq: 988, dur: 0.07, vol: 0.13, send: 0 });
    e.tone({ type: 'square', freq: 1319, t: 0.07, dur: 0.16, vol: 0.14, send: 0 });
  },
  // COMMIT / LOCK-IT-IN = a short celebratory "ka-chunk" slam.
  commit(e) {
    e.tone({ type: 'square', freq: 150, freqTo: 70, dur: 0.16, vol: 0.16, send: 0 });
    e.tone({ type: 'triangle', freq: 300, dur: 0.1, vol: 0.12, send: 0 });
  },
  // REVEAL = the win jackpot (renderer fires win/lose explicitly by outcome).
  reveal(e) {
    playHyperpopWin(e);
  },
  // ADVANCE / PLAY NEXT = quick two-note "insert coin" jingle.
  advance(e) {
    e.tone({ type: 'square', freq: 784, dur: 0.09, vol: 0.13, send: 0 });
    e.tone({ type: 'square', freq: 1175, t: 0.1, dur: 0.2, vol: 0.14, send: 0 });
  },
  // RESET = same insert-coin jingle.
  reset(e) {
    e.tone({ type: 'square', freq: 784, dur: 0.09, vol: 0.13, send: 0 });
    e.tone({ type: 'square', freq: 1175, t: 0.1, dur: 0.2, vol: 0.14, send: 0 });
  },
  // HOVER = tiny square blip.
  hover(e) {
    e.tone({ type: 'square', freq: 740, dur: 0.045, vol: 0.07, send: 0 });
  },
};

// ── Risograph Assembly — print-shop foley (filtered noise + woody thuds, dry) ─
// Ported from the risograph-assembly mockup: paper rustle, typewriter key tick,
// rubber-stamp thunk, print-roller ka-chunk. All foley from filtered NOISE +
// short percussive sine/triangle transients — no tones/melodies. Dry (send 0).

/** Paper rustle — a sheet sliding off the pile; band-passed noise, pitch drifts. */
function risoRustle(e: ThemeWorldAudioEngine): void {
  e.noise({
    filterType: 'bandpass',
    freq: 2300 + Math.random() * 1200,
    q: 0.9,
    dur: 0.13 + Math.random() * 0.04,
    vol: 0.3,
    send: 0,
    shape: 'decay',
  });
}

/** Rubber-stamp THUNK — woody low thud + ink-slap. `scale` hits a touch bigger. */
export function playRisoStamp(e: ThemeWorldAudioEngine, scale = 1, t = 0): void {
  const s = scale;
  e.tone({ type: 'sine', freq: 110 * (s > 1 ? 1.05 : 1), freqTo: 58, t, dur: 0.16, vol: 0.55 * s, send: 0, attack: 0.004 });
  e.tone({ type: 'triangle', freq: 190, freqTo: 90, t, dur: 0.09, vol: 0.24 * s, send: 0, attack: 0.004 });
  e.noise({ filterType: 'highpass', freq: 1400, q: 0.7, t, dur: 0.07, vol: 0.45 * s, send: 0, shape: 'decay' });
}

/** Print-roller KA-CHUNK — two stamp thunks in quick succession (press closing). */
export function playRisoRoller(e: ThemeWorldAudioEngine): void {
  playRisoStamp(e, 1.1, 0);
  playRisoStamp(e, 1.3, 0.11);
}

const risograph: CuePalette = {
  // SELECT / lens flip = paper rustle.
  select(e) {
    risoRustle(e);
  },
  // COMMIT / "stamp this view" = rubber-stamp thunk.
  commit(e) {
    playRisoStamp(e, 1);
  },
  // REVEAL / synthesis filed = print-roller ka-chunk.
  reveal(e) {
    playRisoRoller(e);
  },
  // ADVANCE = a lighter paper rustle.
  advance(e) {
    risoRustle(e);
  },
  // RESET = paper rustle.
  reset(e) {
    risoRustle(e);
  },
  // HOVER = tiny typewriter key tick.
  hover(e) {
    e.noise({ filterType: 'bandpass', freq: 3200 + Math.random() * 600, q: 1.4, dur: 0.02, vol: 0.12, send: 0, shape: 'decay' });
  },
};

// Worlds without a bespoke palette fall back to neon's generic sci-fi cues.
const PALETTES: Partial<Record<ThemeId, CuePalette>> = {
  'neon-oracle': neonOracle,
  obsidian,
  aurora,
  terra,
  hyperpop,
  risograph,
};

export function getCuePalette(worldId: ThemeId): CuePalette {
  return PALETTES[worldId] ?? neonOracle;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook — one engine instance per mount, muted by default.
// ─────────────────────────────────────────────────────────────────────────────

export interface ThemeWorldAudio {
  enabled: boolean;
  toggle: () => void;
  play: (cue: CueName) => void;
  /**
   * Fire a parameterised one-shot directly on the engine (e.g. the aurora
   * pitch-tracked pluck or magnitude-scaled shift-bloom). No-op when muted.
   */
  playWith: (fn: (engine: ThemeWorldAudioEngine) => void) => void;
}

export function useThemeWorldAudio(worldId: ThemeId): ThemeWorldAudio {
  const engineRef = useRef<ThemeWorldAudioEngine | null>(null);
  if (engineRef.current === null) engineRef.current = new ThemeWorldAudioEngine();

  const paletteRef = useRef<CuePalette>(getCuePalette(worldId));
  paletteRef.current = getCuePalette(worldId);

  const [enabled, setEnabled] = useState(false);
  const enabledRef = useRef(false);
  enabledRef.current = enabled;

  useEffect(() => {
    const engine = engineRef.current;
    return () => {
      engine?.dispose();
    };
  }, []);

  const play = useCallback((cue: CueName) => {
    if (!enabledRef.current) return;
    const engine = engineRef.current;
    if (engine) paletteRef.current[cue](engine);
  }, []);

  const playWith = useCallback((fn: (engine: ThemeWorldAudioEngine) => void) => {
    if (!enabledRef.current) return;
    const engine = engineRef.current;
    if (engine) fn(engine);
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      enabledRef.current = next;
      if (next) {
        // resume the context on the first enabling gesture + a confirming cue
        engineRef.current?.resume();
        const engine = engineRef.current;
        if (engine) paletteRef.current.select(engine);
      }
      return next;
    });
  }, []);

  return { enabled, toggle, play, playWith };
}
